import { OrderDirection } from '~api/som_search';

import { NavLink, Link as RouterLink } from 'react-router-dom';

import { Add16 } from '@ingrid/iconography/react';
import {
  Breadcrumb,
  Button,
  InteractiveTable,
  ListLayout,
  PageTitle,
  PageWrapper,
  PaginationProvider,
} from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useDebounce } from '~src/lib/hooks/useDebounce';
import { usePagination } from '~src/main/hooks/usePagination';
import { AppRoute } from '~src/main/routes';

import { useAddressBookListQuery } from '../queries';
import { Filters } from './filters/filters';
import { useAddressBookListFilter } from './filters/useAddressBookListFilter';
import { ListTable } from './list-table';
import { ListTableBar } from './list-table-bar';

export function Page() {
  const { pageIndex, pageSize, direction, setPageSize, setPageIndex } = usePagination();
  const { search } = useAddressBookListFilter();
  const { t } = useTranslation();

  const { data, isFetched, isFetching } = useAddressBookListQuery({
    order_by_direction: direction.toUpperCase() as OrderDirection,
    query: useDebounce(search, 300),
    pageIndex: pageIndex - 1,
    pageSize: pageSize,
  });

  const contacts = (data?.contacts ?? []).filter(c => c.id);

  return (
    <InteractiveTable.Provider allowHighlight keepInactive>
      <PaginationProvider
        onPageSizeChange={setPageSize}
        onPageNoChange={setPageIndex}
        pageSize={pageSize}
        pageNo={pageIndex}
      >
        <PageWrapper>
          <PageTitle.Wrapper>
            <PageTitle.Stack>
              <Breadcrumb aria-label={t('common.breadcrumbs')}>
                <NavLink to="..">{t('common.settings')}</NavLink>
                <a aria-current="page">{t('common.addressBook')}</a>
              </Breadcrumb>
            </PageTitle.Stack>

            <ListLayout.Column style={{ flexDirection: 'row', gap: '1rem' }}>
              <ListLayout.Stack>
                <RoleGuard roles={['transport:edit']} withTooltip>
                  <Button as={RouterLink} to={AppRoute.CREATE}>
                    <Add16 />
                    {t('addressBook.list.addContact')}
                  </Button>
                </RoleGuard>
              </ListLayout.Stack>
            </ListLayout.Column>
          </PageTitle.Wrapper>

          <Filters />

          <section style={{ backgroundColor: 'inherit' }}>
            <ListTableBar />

            <ListTable
              isEmpty={isFetched && contacts.length === 0}
              contacts={contacts || []}
              loadRowCount={pageSize}
              loading={isFetching}
            />
          </section>
        </PageWrapper>
      </PaginationProvider>
    </InteractiveTable.Provider>
  );
}
